/**
 * Plan 12, Task 5.3 — Dropped key object pool.
 *
 * Object pool for dropped key visuals (spec §3: "dropped keys on ground can be
 * picked up while passing"). Keys are tiny gold key shapes that rotate slowly
 * on the Y axis for visual interest.
 *
 * Key shape: cylinder shaft + torus handle — a classic key silhouette.
 * Material: MeshStandardMaterial, bright gold (#FFD700), metallic 0.8, roughness 0.3.
 * Pool starts empty, grows on demand, shrinks never (keys are recycled).
 */

import * as THREE from 'three';
import type { DroppedKeyPile } from '@shared/game/types';

export interface DroppedKeyPool {
  /** Acquire a key mesh from the pool (reuse or create). Position it at (x, y, z). */
  acquire(x: number, y: number, z: number): THREE.Object3D;
  /** Return a key mesh to the pool (hide + reset position). */
  release(key: THREE.Object3D): void;
  /** Sync floating key swarms from authoritative dropped-key state. */
  sync(
    droppedKeys: ReadonlyMap<string, readonly DroppedKeyPile[] | number>,
    playerColors: ReadonlyMap<string, number>,
    getPosition: (nodeId: string) => THREE.Vector3 | null,
    getTopY: (nodeId: string) => number | null,
  ): void;
  /** Sync a single node's floating key swarm after an immediate collection event. */
  syncNode(
    nodeId: string,
    piles: readonly DroppedKeyPile[] | number | undefined,
    playerColors: ReadonlyMap<string, number>,
    getPosition: (nodeId: string) => THREE.Vector3 | null,
    getTopY: (nodeId: string) => number | null,
  ): void;
  /** Number of currently active (visible) keys. */
  readonly activeCount: number;
  /** Per-frame update: rotate active keys for visual interest. */
  update(deltaSeconds: number): void;
  /** Dispose all key geometries + materials. */
  dispose(): void;
}

// Shared geometries + materials allocated once and reused across all key instances.
// They are created on first pool creation and disposed when the pool is disposed.
interface KeyAssets {
  shaftGeo: THREE.CylinderGeometry;
  handleGeo: THREE.TorusGeometry;
  mat: THREE.MeshStandardMaterial;
}

const GOLD_COLOR = new THREE.Color(0xffd700);
const OWNER_TINT_MIX = 1;

/** Build a single key mesh group from shared assets. Scale 0.15 applied to group. */
function buildKeyGroup(assets: KeyAssets): THREE.Group {
  const group = new THREE.Group();

  // Shaft: small cylinder (radiusTop, radiusBottom, height, radialSegments)
  const shaftMesh = new THREE.Mesh(assets.shaftGeo, assets.mat);
  // Position shaft so the handle sits above: shaft center at Y=0, handle centered at Y = height/2 + handle radius
  shaftMesh.position.set(0, 0, 0);
  group.add(shaftMesh);

  // Handle: torus ring at top of shaft
  // Torus is in XZ plane by default; rotate to XY plane so it sits as a ring on top.
  const handleMesh = new THREE.Mesh(assets.handleGeo, assets.mat);
  // Position handle at top of shaft: shaft height = 0.12, half = 0.06; torus radius = 0.04
  handleMesh.position.set(0, 0.06 + 0.04, 0);
  handleMesh.rotation.x = Math.PI / 2;
  group.add(handleMesh);

  // Scale the entire group down to a small key size
  group.scale.setScalar(0.15);

  return group;
}

function buildModelKeyGroup(modelScene: THREE.Group): THREE.Group {
  const wrapper = new THREE.Group();
  const model = modelScene.clone(true);
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  model.position.sub(center);

  const maxAxis = Math.max(size.x, size.y, size.z, 0.0001);
  model.scale.setScalar(0.34 / maxAxis);
  model.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true;
      object.receiveShadow = true;
    }
  });

  wrapper.add(model);
  return wrapper;
}

function phaseFor(nodeId: string, slot: number): number {
  let hash = slot * 2654435761;
  for (let i = 0; i < nodeId.length; i += 1) {
    hash = Math.imul(hash ^ nodeId.charCodeAt(i), 16777619);
  }
  return (hash >>> 0) * 0.000001;
}

function normalizePiles(piles: readonly DroppedKeyPile[] | number | undefined): DroppedKeyPile[] {
  if (typeof piles === 'number') {
    return piles > 0 ? [{ ownerPlayerId: null, amount: piles }] : [];
  }
  return piles == null ? [] : [...piles];
}

function getPileTotal(piles: readonly DroppedKeyPile[] | number | undefined): number {
  if (typeof piles === 'number') return Math.max(0, piles);
  if (piles == null) return 0;
  return piles.reduce((total, pile) => total + Math.max(0, pile.amount), 0);
}

function expandVisualOwners(
  piles: readonly DroppedKeyPile[],
  visualCount: number,
): Array<string | null> {
  const owners: Array<string | null> = [];
  const sorted = piles.filter((pile) => pile.amount > 0);
  if (sorted.length === 0 || visualCount <= 0) return owners;
  const total = getPileTotal(sorted);
  for (const pile of sorted) {
    const count = Math.max(1, Math.round((pile.amount / total) * visualCount));
    for (let i = 0; i < count && owners.length < visualCount; i += 1) {
      owners.push(pile.ownerPlayerId);
    }
  }
  for (let i = 0; owners.length < visualCount; i += 1) {
    owners.push(sorted[i % sorted.length]!.ownerPlayerId);
  }
  return owners;
}

function tintForOwner(
  ownerPlayerId: string | null,
  playerColors: ReadonlyMap<string, number>,
): number | null {
  if (ownerPlayerId == null) return null;
  const playerColor = playerColors.get(ownerPlayerId);
  if (playerColor == null) return null;
  return GOLD_COLOR.clone().lerp(new THREE.Color(playerColor), OWNER_TINT_MIX).getHex();
}

function applyKeyTint(key: THREE.Object3D, color: number | null): void {
  key.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const current = object.material;
    if (!(current instanceof THREE.MeshStandardMaterial)) return;

    const original = (object.userData['droppedKeyOriginalMaterial'] ?? current) as THREE.Material;
    object.userData['droppedKeyOriginalMaterial'] = original;

    const tinted = object.userData['droppedKeyTintMaterial'];
    if (tinted instanceof THREE.Material) {
      tinted.dispose();
      object.userData['droppedKeyTintMaterial'] = undefined;
    }

    if (color == null) {
      object.material = original;
      return;
    }

    const next = current.clone();
    next.color.setHex(color);
    next.map = null;
    next.vertexColors = false;
    next.needsUpdate = true;
    object.material = next;
    object.userData['droppedKeyTintMaterial'] = next;
  });
}

/**
 * Object pool for dropped key visuals.
 * Spec §3: "dropped keys on ground can be picked up while passing".
 */
export function createDroppedKeyPool(
  parentGroup: THREE.Group,
  opts?: { modelScene?: THREE.Group | null },
): DroppedKeyPool {
  // Shared assets — allocated once per pool, disposed with pool.
  const assets: KeyAssets = {
    shaftGeo: new THREE.CylinderGeometry(0.02, 0.02, 0.12, 8),
    handleGeo: new THREE.TorusGeometry(0.04, 0.015, 6, 12),
    mat: new THREE.MeshStandardMaterial({
      color: 0xffd700, // bright gold
      metalness: 0.8,
      roughness: 0.3,
    }),
  };

  const available: THREE.Group[] = [];
  const active = new Set<THREE.Group>();
  const syncedKeys = new Map<string, THREE.Group[]>();
  let disposed = false;
  let elapsedSeconds = 0;

  function createKey(): THREE.Group {
    return opts?.modelScene != null ? buildModelKeyGroup(opts.modelScene) : buildKeyGroup(assets);
  }

  function releaseNodeKeys(nodeId: string): void {
    const keys = syncedKeys.get(nodeId);
    if (keys == null) return;
    for (const key of keys) {
      pool.release(key);
    }
    syncedKeys.delete(nodeId);
  }

  const pool: DroppedKeyPool = {
    get activeCount(): number {
      return active.size;
    },

    acquire(x: number, y: number, z: number): THREE.Object3D {
      let key: THREE.Group;
      if (available.length > 0) {
        key = available.pop()!;
      } else {
        key = createKey();
      }
      key.position.set(x, y, z);
      key.userData['droppedKeyBaseY'] = y;
      key.userData['droppedKeyPhase'] = Math.random() * Math.PI * 2;
      key.visible = true;
      active.add(key);
      parentGroup.add(key);
      return key;
    },

    release(key: THREE.Object3D): void {
      const g = key as THREE.Group;
      if (!active.has(g)) return;
      active.delete(g);
      parentGroup.remove(g);
      g.visible = false;
      applyKeyTint(g, null);
      available.push(g);
    },

    update(deltaSeconds: number): void {
      elapsedSeconds += deltaSeconds;
      for (const key of active) {
        key.rotation.y += deltaSeconds * 1.5;
        const baseY = key.userData['droppedKeyBaseY'];
        const phase = key.userData['droppedKeyPhase'];
        if (typeof baseY === 'number' && typeof phase === 'number') {
          key.position.y = baseY + Math.sin(elapsedSeconds * 1.7 + phase) * 0.045;
          key.rotation.z = Math.sin(elapsedSeconds * 1.2 + phase) * 0.08;
        }
      }
    },

    sync(
      droppedKeys: ReadonlyMap<string, readonly DroppedKeyPile[] | number>,
      playerColors: ReadonlyMap<string, number>,
      getPosition: (nodeId: string) => THREE.Vector3 | null,
      getTopY: (nodeId: string) => number | null,
    ): void {
      for (const nodeId of [...syncedKeys.keys()]) {
        if (getPileTotal(droppedKeys.get(nodeId)) <= 0) {
          releaseNodeKeys(nodeId);
        }
      }

      for (const [nodeId, rawPiles] of droppedKeys.entries()) {
        pool.syncNode(nodeId, rawPiles, playerColors, getPosition, getTopY);
      }
    },

    syncNode(
      nodeId: string,
      rawPiles: readonly DroppedKeyPile[] | number | undefined,
      playerColors: ReadonlyMap<string, number>,
      getPosition: (nodeId: string) => THREE.Vector3 | null,
      getTopY: (nodeId: string) => number | null,
    ): void {
      const piles = normalizePiles(rawPiles);
      const amount = getPileTotal(piles);
      const visualCount = Math.min(Math.max(Math.floor(amount), 0), 12);
      if (visualCount <= 0) {
        releaseNodeKeys(nodeId);
        return;
      }

      const position = getPosition(nodeId);
      const topY = getTopY(nodeId);
      if (position == null || topY == null) {
        releaseNodeKeys(nodeId);
        return;
      }

      releaseNodeKeys(nodeId);
      const keys: THREE.Group[] = [];
      const owners = expandVisualOwners(piles, visualCount);
      const radius = visualCount === 1 ? 0 : Math.min(0.34, 0.14 + visualCount * 0.012);
      for (let i = 0; i < visualCount; i += 1) {
        const ring = Math.floor(i / 6);
        const ringOffset = ring * 0.07;
        const angle = (i / visualCount) * Math.PI * 2 + ring * 0.8;
        const key = pool.acquire(
          position.x + Math.cos(angle) * (radius + ringOffset),
          topY + 0.36 + (i % 3) * 0.018,
          position.z + Math.sin(angle) * (radius + ringOffset),
        ) as THREE.Group;
        key.rotation.set(0.22, angle + Math.PI * 0.5, -0.18);
        key.userData['droppedKeyBaseY'] = key.position.y;
        key.userData['droppedKeyPhase'] = phaseFor(nodeId, i);
        applyKeyTint(key, tintForOwner(owners[i] ?? null, playerColors));
        keys.push(key);
      }
      syncedKeys.set(nodeId, keys);
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      syncedKeys.clear();

      // Release all active keys back to available and clear
      for (const key of active) {
        applyKeyTint(key, null);
        parentGroup.remove(key);
      }
      for (const key of available) {
        applyKeyTint(key, null);
      }
      active.clear();
      available.length = 0;

      // Dispose shared assets
      assets.shaftGeo.dispose();
      assets.handleGeo.dispose();
      assets.mat.dispose();
    },
  };

  return pool;
}
